import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Column, Row } from 'hedron';
import intersectionWith from 'lodash/intersectionWith';
import Router from 'next/router';
import isEqual from 'lodash/isEqual';
import { Wrapper } from '../css_global/utils.css';
import BackToTopButton from '../../components/BackToTopButton';
import FiltersModal from '../components/FiltersModal';
import OptionSelector from '../components/OptionSelector';
import PageHeading from '../components/PageHeading';
import ProductListPaginationButtons from '../components/PaginationButtons/ProductListPaginationButtons';
import DescriptionAccordion from '../../components_desktop/components/DescriptionAccordion';
import Button from '../../components_common/Button';
import NoResults from '../../components_common/NoResults';
import SearchKeyword from '../../components_common/SearchKeyword';
import SearchResults from '../../components_common/SearchResults';
import {
  changeUrlFiltersVisibility,
  defaultPageSize,
  easeInOutQuint,
  getProductsViewedData,
  PAGE_BRAND_DETAIL,
  ReactPixel,
  scrollToY,
  searchTreeForCategoryWithSizes,
  separateProductDataIntoRows,
  sortOptions,
  onSaleParam,
  track,
} from '../../helpers';
import { generateProductRows } from '../../helpers/productRows';
import { MODAL_FILTERS } from '../../helpers/trackingStringValues';
import { callBackend, changeOffset, sort } from '../../common/actions';
import { FilterTypes, generateFiltersModel, SeasonData } from '../../common/models';
import { allModes, designerMode } from '../../helpers/productListModes';
import { fdSet } from '../../helpers/fdHelper';
import { buildSearchProductsFromUrl, isShoesCategory } from '../../helpers/search_helpers';
import { SizeData } from '../../common/models/SizeData';
import { hideFilters, showFilters } from '../../modules/manageFiltersModal';
import TrackingEvents from '../../common/models/TrackingEvents';
import ProductListMicrodata from '../../components_common/Microdata/ProductListMicrodata';
import { sanitizeBrandDescription } from '../../components/ProductList/sanitizers';
import { normalizeCategoryDescription } from '../../components/ProductList/normalizers';

const ALLOWED_PAGE_BEFORE_REDIRECT = 30;
const ROWS_TO_LOAD_EAGERLY_COUNT = 2;

const mapStateToProps = ({
  countryCode,
  api: {
    filterSettings,
    description,
    brandName,
    currencySymbol,
    categoryName,
    categoryPath,
    categoryUrl,
    location,
    products,
    s3BasePath,
    offset,
    query,
    sortFilterSelect,
    total,
  },
  isFiltersModalVisible,
}) => ({
  countryCode,
  filterSettings,
  description,
  brandName,
  currencySymbol,
  categoryName,
  categoryPath,
  categoryUrl,
  location,
  isFiltersModalVisible,
  products,
  s3BasePath,
  offset,
  query,
  sortFilterSelect,
  total,
});

const mapDispatchToProps = dispatch => ({
  handleOffsetChange: (offset, mode) => dispatch(changeOffset(offset, mode)),
  handleSort: (sortKey, sortText, mode) => dispatch(sort(sortKey, sortText, mode)),
  searchProducts: (url, mode) => dispatch(callBackend(url, mode)),
  handleOpeningModal: () => dispatch(showFilters()),
  handleClosingModal: () => dispatch(hideFilters()),
});

class ProductList extends React.Component {
  constructor() {
    super();
    this.calculateRefinedByTotal = this.calculateRefinedByTotal.bind(this);
    this.calculateSelectedSeasonsCount = this.calculateSelectedSeasonsCount.bind(this);
    this.handleSortClick = this.handleSortClick.bind(this);
    this.pageSelected = this.pageSelected.bind(this);
    this.renderDesignerDescription = this.renderDesignerDescription.bind(this);
    this.state = {};
  }

  componentDidMount() {
    const { searchProducts, mode, page, categoryPath, query, categoryName, brandName } = this.props;
    const productsViewData = getProductsViewedData(categoryPath, categoryName, {
      query,
      isBrand: page === PAGE_BRAND_DETAIL,
      brandName,
    });
    const onSalePageOne = window.location.search.slice(-9) === onSaleParam;
    productsViewData.product = { on_sale: onSalePageOne };
    track(TrackingEvents.productsViewed(), productsViewData);

    Router.beforePopState(({ as }) => {
      // manage browser back and foward buttons
      const url = buildSearchProductsFromUrl(as, mode);
      searchProducts(url, mode, as);
      return true;
    });

    this.setState({ onSalePageOne });
    Router.router.events.on('routeChangeComplete', this.handleUrlChange);

    fdSet();
  }

  componentDidUpdate(oldProps) {
    const { products } = this.props;
    if (!isEqual(oldProps.products, products)) {
      scrollToY(0, 1500, easeInOutQuint);
    }
  }

  handleUrlChange = url => {
    const { handleClosingModal, handleOpeningModal, isFiltersModalVisible } = this.props;
    if (url.indexOf('filters=true') !== -1 && !isFiltersModalVisible) {
      handleOpeningModal();
    } else if (url.indexOf('filters=true') === -1 && isFiltersModalVisible) {
      handleClosingModal();
    }
  };

  pageSelected(page) {
    if (page > ALLOWED_PAGE_BEFORE_REDIRECT) {
      window.location.href = '/refine_your_search';
      return;
    }
    const { mode } = this.props;
    const offset = (page - 1) * defaultPageSize;
    this.props.handleOffsetChange(offset, mode);
  }

  handleSortClick(e) {
    const { value } = e.target;
    const { text } = e.target.options[e.target.selectedIndex];
    const { handleSort, mode } = this.props;
    handleSort(value, text, mode);
  }

  calculateSelectedSeasonsCount() {
    const { availableSeasons, currentSeason, seasons } = this.props.filterSettings;

    const selectedSeasonData = seasons.map(el => SeasonData.fromSeasonString(el));
    const allSeasonsData = generateFiltersModel(
      SeasonData.fromSeasonString(currentSeason),
      availableSeasons.map(el => SeasonData.fromSeasonString(el)),
      selectedSeasonData,
    );

    return allSeasonsData.filter(el => intersectionWith(el.data, selectedSeasonData, isEqual).length !== 0).length;
  }

  calculateRefinedByTotal() {
    const { mode, filterSettings } = this.props;
    const { brandsIds, sizes } = filterSettings;

    let refinedByTotal = this.calculateSelectedSeasonsCount();

    refinedByTotal += sizes.filter(el => SizeData.universal() !== el).length;
    const brandPage = mode === designerMode;

    if (!brandPage) {
      refinedByTotal += brandsIds.length;
    }

    return refinedByTotal;
  }

  toggleFiltersModalVisibility = () => {
    const { handleClosingModal, handleOpeningModal, isFiltersModalVisible, mode } = this.props;
    if (isFiltersModalVisible) {
      const { categoryPath, categoryName, query, brandName, page } = this.props;
      const productsViewData = getProductsViewedData(categoryPath, categoryName, {
        query,
        isBrand: page === PAGE_BRAND_DETAIL,
        brandName,
      });
      productsViewData.product = { on_sale: window.location.search.slice(-9) === onSaleParam };
      track(TrackingEvents.productsViewed(), productsViewData);
      handleClosingModal();
    } else {
      track(TrackingEvents.modalOpened(), { ui: { type: MODAL_FILTERS } });
      handleOpeningModal();
    }
    changeUrlFiltersVisibility(!isFiltersModalVisible, mode);
  };

  renderDesignerDescription(brandPage) {
    const { countryCode, description, s3BasePath } = this.props;

    return (
      description && (
        <div style={{ borderBottomColor: '#ccc', borderBottomStyle: 'solid', borderBottomWidth: '1px' }}>
          <DescriptionAccordion
            dataCy="accordion-description-designer"
            collapsed
            isDesktop={false}
            s3BasePath={s3BasePath}
            text={
              brandPage ? sanitizeBrandDescription(description) : normalizeCategoryDescription(countryCode, description)
            }
          />
        </div>
      )
    );
  }

  render() {
    const {
      brandName,
      description,
      filterSettings,
      currencySymbol,
      categoryName,
      isFiltersModalVisible,
      freeShipping,
      location,
      products,
      offset,
      query,
      s3BasePath,
      sortFilterSelect,
      total,
      mode,
    } = this.props;

    const { currentCategoryId, categoryTree } = filterSettings;

    const { onSalePageOne, showFilters } = this.state;

    const refinedByTotal = this.calculateRefinedByTotal();
    const refinedByLabel = refinedByTotal ? `Refine by (${refinedByTotal})` : 'Refine By';

    const productRows = separateProductDataIntoRows(products, 4);
    const currentSkip = offset || 0;

    const baseFilters = [FilterTypes.category(), FilterTypes.season(), FilterTypes.price()];

    const brandPage = mode === designerMode;
    const allFilters = brandPage ? baseFilters : baseFilters.concat(FilterTypes.designer());
    if (searchTreeForCategoryWithSizes(categoryTree, currentCategoryId)) {
      allFilters.push(FilterTypes.size());
    }

    const titleName = brandPage ? brandName : categoryName;

    return (
      <>
        <Wrapper>
          <BackToTopButton />
          <PageHeading text={titleName} />
          <Row alignItems="center">
            <Column fluid xs={6} sm={6} md={6}>
              <Button
                dataCy="button-refine-by"
                noSpacing
                onClick={this.toggleFiltersModalVisibility}
                title={refinedByLabel}
                marginButton="5% 2.5% 5% 5%"
              />
            </Column>
            <Column fluid xs={6} sm={6} md={6}>
              <OptionSelector
                dataCy="select-sort-products"
                handleClick={this.handleSortClick}
                margin="5% 5% 5% 2.5%"
                name="sort_field"
                height="35px"
                font={'"Avenir Light", "Arial", sans-serif'}
                options={sortOptions}
                s3BasePath={s3BasePath}
                selectedOption={sortFilterSelect}
                textAlign="center"
                uppercase
              />
            </Column>
          </Row>
          {this.renderDesignerDescription(brandPage)}
          {query ? <SearchKeyword noResults={total === 0} keyword={query} /> : null}
          {total !== 0 ? (
            <div style={{ padding: 10, textAlign: 'center' }}>
              <SearchResults />
            </div>
          ) : null}
          <Wrapper>
            {productRows.length ? (
              generateProductRows(
                productRows,
                s3BasePath,
                currencySymbol,
                false,
                freeShipping,
                ROWS_TO_LOAD_EAGERLY_COUNT,
              )
            ) : (
              <div>{!query ? <NoResults /> : null}</div>
            )}
          </Wrapper>
          {productRows.length ? (
            <ProductListPaginationButtons
              page={currentSkip / defaultPageSize + 1}
              pageSelected={this.pageSelected}
              s3BasePath={s3BasePath}
              totalPages={Math.ceil(total / defaultPageSize)}
            />
          ) : null}
          <FiltersModal
            availableFilters={allFilters}
            s3BasePath={s3BasePath}
            isOpen={isFiltersModalVisible}
            isShoesCategory={isShoesCategory(currentCategoryId)}
            toggle={this.toggleFiltersModalVisibility}
            mode={mode}
          />
        </Wrapper>
        <ProductListMicrodata products={products} numberOfItems={total} {...location} />
      </>
    );
  }
}

ProductList.propTypes = {
  description: PropTypes.string,
  brandName: PropTypes.string,
  categoryName: PropTypes.string.isRequired,
  categoryUrl: PropTypes.string.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  filterSettings: PropTypes.object.isRequired,
  freeShipping: PropTypes.bool.isRequired,
  handleClosingModal: PropTypes.func.isRequired,
  handleOpeningModal: PropTypes.func.isRequired,
  handleOffsetChange: PropTypes.func,
  handleSort: PropTypes.func.isRequired,
  isFiltersModalVisible: PropTypes.bool.isRequired,
  offset: PropTypes.number.isRequired,
  onSale: PropTypes.bool.isRequired,
  products: PropTypes.array.isRequired,
  query: PropTypes.string,
  s3BasePath: PropTypes.string.isRequired,
  sortFilterSelect: PropTypes.string.isRequired,
  total: PropTypes.number.isRequired,
  mode: PropTypes.oneOf(allModes).isRequired,
  searchProducts: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductList);
